import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { NON_SUPPLIER_ROLES } from "src/constants/permissions";

import { useAuthUser } from "@features/auth";
import { useMutateError } from "@features/errors";
import { useOptionalDistributorOrderingCtx } from "@features/orderingCtx/DistributorOrderingContext";
import { DistributorOrderingCtx } from "@features/orderingCtx/OrderingCtx";
import { getOrderSetAttributesFromCtx } from "@features/orderingCtx/helpers";
import { isActiveDraftOrderSet } from "@features/orders/helpers";
import { Order } from "@models/Order";
import { OrderSet, OrderSetType } from "@models/OrderSet";
import { OrderSetVariant } from "@models/OrderSetVariant";
import { Channel } from "@models/Program";
import client, { RequestParams } from "@services/api";
import asyncPool from "@utils/asyncPool";
import { buildPaginatedQuery } from "@utils/reactQuery";
import mapPaginatedRows from "@utils/reactQuery/mapPaginatedRows";
import { Paginated, QueryOptions } from "@utils/reactQuery/types";
import useRoleIs from "@utils/useRoleIs";

import {
  draftOrderSetsParams,
  getDraftOrderSetIdentity,
} from "../ordering/draftOrderSetIdentity";
import { resolveDraftOrderSetId } from "../ordering/rememberedDraftOrderSets";

export const orderSetsKeyFactory = createQueryKeys("order-sets", {
  detail: (orderSetId) => ({
    queryKey: [orderSetId],
    queryFn: () =>
      client.get<OrderSet>(`order-sets/${orderSetId}`).then((res) => res!.data),
    contextQueries: {
      orders: {
        queryKey: null, // ['order-sets', 'detail', orderSetId, 'orders']
        queryFn: () =>
          client
            .get<Order[]>("orders", {
              params: { filter: { orderSetId }, skipPagination: true },
            })
            .then((res) => res.data),
      },
    },
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () => client.get<OrderSet[]>("order-sets", { params }),
  }),
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client.get<OrderSet[]>("order-sets", { params }).then((res) => res.data),
  }),
});

export const useOrderSetQuery = (
  orderSetId?: string | null,
  options?: QueryOptions<OrderSet>
) => {
  return useQuery({
    ...orderSetsKeyFactory.detail(orderSetId),
    enabled: !!orderSetId,
    ...options,
  });
};

export const useOrderSetsPaginated = buildPaginatedQuery(
  orderSetsKeyFactory.paginated
);

export const useDraftOrderSetsQuery = () => {
  const { id } = useAuthUser();
  const roleIs = useRoleIs();
  return useOrderSetsPaginated(draftOrderSetsParams(id), {
    enabled: roleIs([...NON_SUPPLIER_ROLES, "planning-tool-supplier"]),
  });
};

type CreateOrderSetPayload = {
  type: OrderSetType;
  orderWindowId?: string;
  programId?: string;
  sportsVariantOptionIds?: (string | number)[];

  /** channel passed on mutate may override channel from the context */
  channel?: Channel;
};

/** The ordering context a create happens in, resolved from hooks by callers. */
type CreateOrderSetContext = {
  userId: string;
  channel: string;
  territory?: string | null;
  orderingCtx?: DistributorOrderingCtx | null;
};

/**
 * The create request and its cache maintenance, as plain functions: the
 * add-variants mutation creates a draft inside its own `mutationFn`, and
 * nesting a second mutation hook there would report every failure twice.
 */
const postOrderSet = (
  {
    orderWindowId,
    programId,
    sportsVariantOptionIds,
    ...data
  }: CreateOrderSetPayload,
  { channel, territory, orderingCtx }: CreateOrderSetContext
) => {
  const fromDistCtx =
    orderingCtx &&
    getOrderSetAttributesFromCtx(orderingCtx, channel, programId);
  return client
    .post<OrderSet>("order-sets", {
      __type: "order-set",
      ...(fromDistCtx ?? {
        isForDistributor: false,
        channel,
        allRegions: !territory,
        programId,
        ...(territory && { territoryId: territory }),
      }),
      orderCalendarMonthId: orderWindowId,
      sportsVariantOptionIds: sportsVariantOptionIds?.map(Number) ?? [],
      ...data,
    })
    .then((res) => res.data);
};

const cacheCreatedOrderSet = (
  queryClient: QueryClient,
  orderSet: OrderSet,
  /**
   * Params of the draft list this order-set belongs to. Omitted by creates that
   * navigate straight to the new order-set, which read no draft list.
   */
  listParams?: RequestParams
) => {
  // Seed the detail before the catalog or the next queued add reads it.
  queryClient.setQueryData(orderSetsKeyFactory.detail(orderSet.id).queryKey, {
    ...orderSet,
    orderSetVariants: orderSet.orderSetVariants ?? [],
  });
  queryClient.setQueryData(
    orderSetsKeyFactory.detail(orderSet.id)._ctx.orders.queryKey,
    []
  );
  // Seed its own draft list too, and only that one: a catalog reads its cart
  // through this list, so until the refetch lands the item just added would
  // look absent and "Add to cart" would flicker back to enabled. Lists such as
  // approvals page over everyone's order sets and pick it up from the server.
  if (listParams) {
    queryClient.setQueryData<Paginated<OrderSet>>(
      orderSetsKeyFactory.paginated(listParams).queryKey,
      mapPaginatedRows<OrderSet>((rows, page) =>
        page === 0 ? [...rows, orderSet] : rows
      )
    );
  }
  queryClient.invalidateQueries({
    queryKey: orderSetsKeyFactory.paginated._def,
  });
  queryClient.invalidateQueries({ queryKey: orderSetsKeyFactory.list._def });
};

/** A create that fails other than validation may still have written. */
const invalidateAfterFailedCreate = (
  queryClient: QueryClient,
  err: { response?: { status?: number } }
) => {
  if (err.response?.status === 422) return;
  queryClient.invalidateQueries({
    queryKey: orderSetsKeyFactory.paginated._def,
  });
};

const useCreateOrderSetContext = (): CreateOrderSetContext => {
  const { id: userId, channel, territory } = useAuthUser();
  const distCtx = useOptionalDistributorOrderingCtx();
  return { userId, channel, territory, orderingCtx: distCtx?.orderingCtx };
};

export const useCreateOrderSetMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();
  const context = useCreateOrderSetContext();

  return useMutation({
    mutationFn: (payload: CreateOrderSetPayload) =>
      postOrderSet(payload, context),
    onSuccess: (orderSet) => cacheCreatedOrderSet(queryClient, orderSet),
    onError: (err) => {
      setError(err, "Create Order Set");
      invalidateAfterFailedCreate(queryClient, err);
    },
  });
};

export type CreateOrderSetVariantsPayload = {
  /** Omit to add to (or create) the current context's draft. */
  orderSetId?: string | null;
  variantIds: string[];
  orderType: OrderSetType;
  programId?: string;
  orderWindowId?: string;
};

export const useCreateOrderSetVariantsMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();
  const context = useCreateOrderSetContext();
  const { userId, channel, territory, orderingCtx } = context;

  return useMutation({
    // Serializes adds for this catalog across components, so the variant merge
    // in onSuccess is never a concurrent read-modify-write. React Query fixes
    // the scope per hook, so it cannot narrow to the per-payload identity key
    // (program, order window); over-serializing is the safe direction.
    scope: {
      id: `order-set-variants:${orderingCtx?.id ?? "internal"}:${channel}:${territory ?? ""}`,
    },
    mutationFn: async ({
      orderSetId,
      variantIds,
      orderType,
      programId,
      orderWindowId,
    }: CreateOrderSetVariantsPayload) => {
      const { key, matches, listParams } = getDraftOrderSetIdentity({
        orderType,
        channel,
        territory,
        orderingCtx,
        orderWindowId,
        programId,
      });

      const osId =
        orderSetId ??
        (await resolveDraftOrderSetId({
          // Drafts are per user, and nothing clears the remembered ids but a
          // page load, so the signed-in user scopes the key.
          key: `${userId}:${key}`,
          // Read the one list this draft would appear in by exact key: lists
          // such as approvals page over everyone's order sets.
          findCachedDraftId: () =>
            (
              queryClient.getQueryData<Paginated<OrderSet>>(
                orderSetsKeyFactory.paginated(listParams(userId)).queryKey
              )?.pages ?? []
            )
              .flatMap((page) => page.data ?? [])
              .find(matches)?.id,
          // A remembered id is only a fallback, and has to be backed by a live
          // detail: deleting drops the detail and submitting flips its status,
          // so both retire the draft.
          isDraftStillUsable: (id) => {
            const detail = queryClient.getQueryData<OrderSet>(
              orderSetsKeyFactory.detail(id).queryKey
            );
            return !!detail && isActiveDraftOrderSet(detail);
          },
          createDraft: async () => {
            try {
              const orderSet = await postOrderSet(
                { type: orderType, programId, orderWindowId },
                context
              );
              cacheCreatedOrderSet(queryClient, orderSet, listParams(userId));
              return orderSet.id;
            } catch (err) {
              invalidateAfterFailedCreate(queryClient, err as never);
              throw err;
            }
          },
        }));

      const res = await asyncPool(5, variantIds, (variantId) =>
        client
          .post<OrderSetVariant>(`order-set-variants`, {
            __type: "order-set-variant",
            programId,
            orderSet: {
              id: osId,
            },
            variant: {
              id: variantId,
            },
            relationshipNames: ["variant", "orderSet"],
          })
          .then((res) => res.data)
      );
      if (res.errors) {
        // Some variants may have landed; refetch rather than guess.
        await queryClient.invalidateQueries({
          queryKey: orderSetsKeyFactory.detail(osId).queryKey,
        });
        throw res.errors[0];
      }
      return { id: osId, orderSetVariants: res.results };
    },
    onSuccess: async ({ id, orderSetVariants }) => {
      const detailKey = orderSetsKeyFactory.detail(id).queryKey;
      // cancel requests for the same order set
      await queryClient.cancelQueries({ queryKey: detailKey });
      const merged = queryClient.setQueryData<OrderSet>(
        detailKey,
        (os) =>
          os && {
            ...os,
            orderSetVariants: [
              ...(os.orderSetVariants ?? []),
              ...orderSetVariants,
            ],
          }
      );
      // A draft from the list cache may have no detail yet, and the cancel
      // above stopped that fetch. With nothing to merge into, the variants
      // would be dropped and the cart would look empty, so fetch instead.
      if (!merged) {
        await queryClient.invalidateQueries({ queryKey: detailKey });
      }
    },
    onError: (err) => setError(err, "Create Order Set Variant"),
  });
};

export const useApproveOrderSetsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderSetIds: string[]) => {
      const res = await asyncPool(5, orderSetIds, (orderSetId) =>
        client.post(
          `order-sets/${orderSetId}/approve`,
          {},
          { timeout: 10 * 60_000 }
        )
      );
      if (res.errors) {
        throw res.errors[0];
      }
    },
    onSettled: () =>
      Promise.all([
        // Every detail rather than the approved ids: one scan instead of one
        // per selected order set, and only an open one refetches.
        queryClient.invalidateQueries({
          queryKey: orderSetsKeyFactory.detail._def,
        }),
        queryClient.invalidateQueries({
          queryKey: orderSetsKeyFactory.paginated._def,
        }),
      ]),
  });
};

export const useDenyOrderSetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => {
      return client.post(
        `order-sets/${id}/deny`,
        { data: { note } },
        { serializeBody: false }
      );
    },
    onSuccess: (_, { id }) =>
      queryClient.setQueriesData<Paginated<OrderSet>>(
        { queryKey: orderSetsKeyFactory.paginated._def, type: "active" },
        mapPaginatedRows((rows: OrderSet[]) =>
          rows.filter((os) => os.id !== id)
        )
      ),
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: orderSetsKeyFactory.paginated._def,
      });
    },
    onSettled: (_data, _error, { id }) =>
      Promise.all([
        // The detail and its orders still report the pre-deny status, so mark
        // them stale. Only stale: denying from the order set navigates away, and
        // refetching here would delay that for data nobody reads.
        queryClient.invalidateQueries({
          queryKey: orderSetsKeyFactory.detail(id).queryKey,
          refetchType: "none",
        }),
        queryClient.invalidateQueries({
          queryKey: orderSetsKeyFactory.paginated._def,
          type: "inactive",
        }),
      ]),
  });
};
